// FailedExtractionsPanel - a project-level banner at the top of the Files tab
// listing EVERY upload whose extraction failed, each with an inline Re-extract
// link. It reads the same `/uploads/list` data the "Files N" failed badge
// counts (ProjectHubLayout), so a failed file can never be counted-but-hidden:
// if the badge counts it, this panel shows it - regardless of which sub-section
// (Drive tree / Gmail listener tree / directly-attached list) would otherwise
// render (or hide, e.g. inside a collapsed same-subject group) the row.
//
// This closes the gap behind the recurring "badge says 1 but nothing shows as
// failed" reports (DNG2, DLU2): the failed row was real but tucked away in a
// surface the operator wasn't looking at.
import { useUploadsList } from "../api";
import { displayUploadName } from "../utils/uploadDisplayName";
import { ReextractLink } from "./ReextractLink";
import { SightingList } from "./SightingList";

interface Props {
  projectId: string;
}

export const FailedExtractionsPanel = ({ projectId }: Props): JSX.Element | null => {
  const { data: uploads } = useUploadsList(projectId);

  // Same exclusion as ExtractionSummaryBar's counts, which sit directly above this
  // panel: a quarantined row keeps whatever extraction_status it had when it was
  // quarantined, so counting it here and not there made the headline "N failed"
  // disagree with the list immediately below it.
  const failed = (uploads ?? []).filter(
    (u) =>
      u.extraction_status === "failed" &&
      u.status !== "removed" &&
      u.status !== "quarantined",
  );
  if (failed.length === 0) return null;

  return (
    <div
      role="alert"
      style={{
        marginBottom: 20,
        padding: "12px 14px",
        borderRadius: 8,
        background: "transparent",
        border: "1px solid #f87171",
        color: "#991b1b",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        ✗ {failed.length} file{failed.length === 1 ? "" : "s"} failed extraction
      </div>
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {failed.map((u) => (
          <li
            key={u.upload_id}
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            {/* The file's provenance rendered the SAME way as every other panel
                (quarantine / wrong-site / Files-tab row), via SightingList, so the
                operator sees the proper "Gmail sync: email thread ... received on
                ..." line instead of a bare filename + raw source token. */}
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontWeight: 500 }}>
                {displayUploadName(u.original_name)}
              </span>
              <span style={{ fontSize: 11, color: "#b91c1c" }}>
                <SightingList sightings={u.sightings ?? []} />
              </span>
            </span>
            <ReextractLink
              projectId={projectId}
              uploadId={u.upload_id}
              extractionStatus={u.extraction_status}
            />
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 8, color: "#b91c1c", fontSize: 12 }}>
        Re-extract retries against the stored copy (no re-fetch). Most failures
        are transient - a retry usually clears them.
      </div>
    </div>
  );
};
